import React, { useState, useEffect } from 'react';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { pdf } from '@react-pdf/renderer';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../shared/constants/btnNames';
import deleteIcon from '../../asset/icon/delete.png';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { AlertPosition, AlertType, ExportFormat, PdfLayout, TextAlignment, WorkContentMode } from '../../shared/enum/EnumRepository';
import logo from '../../asset/gnb/logo_fit.png';
import WorkListA4PreviewVertical from './WorkListA4PreviewVertical';
import WorkListA4PreviewHorizontal, {
  getWorkListFirstPageCapacity,
} from './WorkListA4PreviewHorizontal';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { useExportStationWorkStore } from '../../shared/store/exportStationWorkStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import DocumentWorkListHorizontal, {
  buildWorkListBilingualTableRows,
  buildWorkListPdfTableRows,
  type WorkListPdfTableRow,
} from './DocumentWorkListHorizontal';
import DocumentWorkListVertical from './DocumentWorkListVertical';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { useSettingStore } from '../../shared/store/settingStore';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';
import { buildWorkListXlsxBlob } from './WorkListWorkbookExcel';
import { WORK_LIST_EXPORT_MAX_ROWS } from '../../shared/constants/limits';
import WorkListExcelPreviewVertical from './WorkListExcelPreviewVertical';
import { shouldShowWorkArtist } from '../../shared/util/workArtist';

interface WorkListPdfModalSubProps {
  onBack: () => void;
  onCloseModal: () => void;
}

const WORK_LIST_PDF_BASE_ITEMS_PER_PAGE = 10;
const BILINGUAL_EXPORT_MODE = 'BILINGUAL' as const;
type WorkListExportContentMode = WorkContentMode | typeof BILINGUAL_EXPORT_MODE;

const WorkListPdfModalCreate: React.FC<WorkListPdfModalSubProps> = ({
  onBack,
  onCloseModal,
}) => {
  const { selectedDataList } = useExportStationWorkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const [pdfLayout, setPdfLayout] = useState<PdfLayout>(PdfLayout.Horizontal);
  const { getSelectedWorkListForExport } = useWorkListActions();
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const setting = useSettingStore((s) => s.setting);
  const username = setting?.username ?? '';
  const includeArtist = shouldShowWorkArtist(setting);
  const [exportContentMode, setExportContentMode] =
    useState<WorkListExportContentMode>(workContentMode);
  const [pdfOrXlsx, setPdfOrXlsx] = useState<ExportFormat>(ExportFormat.PDF);
  const [includePrice, setIncludePrice] = useState(false);
  const [includeNote, setIncludeNote] = useState(false);
  const [singleLineCaption, setSingleLineCaption] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [gallery, setGallery] = useState('');
  const [excelPreviewRows, setExcelPreviewRows] = useState<WorkListPdfTableRow[]>([]);
  const [excelPreviewLoading, setExcelPreviewLoading] = useState(false);

  useEffect(() => {
      void getSelectedWorkListForExport(selectedDataList.map((item) => item.workId));
  }, []);

  useEffect(() => {
    if (pdfOrXlsx !== ExportFormat.XLSX) return undefined;
    let cancelled = false;
    setExcelPreviewLoading(true);
    const rowsPromise =
      exportContentMode === BILINGUAL_EXPORT_MODE
        ? buildWorkListBilingualTableRows(selectedDataList, includeArtist)
        : buildWorkListPdfTableRows(
            selectedDataList,
            exportContentMode,
            true,
            true,
            includeArtist,
          );
    void rowsPromise.then((rows) => {
      if (cancelled) return;
      setExcelPreviewRows(rows);
      setExcelPreviewLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [pdfOrXlsx, selectedDataList, exportContentMode, singleLineCaption, includeArtist]);

  const captionHeaderLabel = getModalMessage('WORK_LIST_PDF_CAPTION_COLUMN_HEADER');

  const hadleChangePdfLayout = (layout: PdfLayout) => {
    setPdfLayout(layout);
  };

  const sanitizeWorkListFileStem = (value: string) =>
    value.replace(/[/\\?%*:|"<>]/g, '-').trim().slice(0, 80);

  const handleGenerate = async () => {
    try {
      if (selectedDataList.length > WORK_LIST_EXPORT_MAX_ROWS) {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('WORK_LIST_EXPORT_MAX_ITEMS', {
            maxCount: WORK_LIST_EXPORT_MAX_ROWS,
          }),
        });
        return;
      }

      const tableRows =
        pdfOrXlsx === ExportFormat.XLSX && exportContentMode === BILINGUAL_EXPORT_MODE
          ? await buildWorkListBilingualTableRows(selectedDataList, includeArtist)
          : await buildWorkListPdfTableRows(
              selectedDataList,
              exportContentMode === BILINGUAL_EXPORT_MODE
                ? WorkContentMode.Orig
                : exportContentMode,
              true,
              true,
              includeArtist,
            );
      let blob: Blob;
      let fileExt: 'pdf' | 'xlsx';
      if (pdfOrXlsx === ExportFormat.XLSX) {
        blob = await buildWorkListXlsxBlob(tableRows, {
          includeArtist,
          includePrice,
          includeNote,
          singleLineCaption,
          captionHeaderLabel,
        });
        fileExt = 'xlsx';
      } else {
        const pdfDoc =
          pdfLayout === PdfLayout.Vertical ? (
            <DocumentWorkListVertical
              title={title}
              subtitle={subtitle}
              gallery={gallery}
              baseItemsPerPage={WORK_LIST_PDF_BASE_ITEMS_PER_PAGE}
              includeArtist={includeArtist}
              includePrice={includePrice}
              includeNote={includeNote}
              tableRows={tableRows}
            />
          ) : (
            <DocumentWorkListHorizontal
              title={title}
              subtitle={subtitle}
              gallery={gallery}
              baseItemsPerPage={WORK_LIST_PDF_BASE_ITEMS_PER_PAGE}
              includeArtist={includeArtist}
              includePrice={includePrice}
              includeNote={includeNote}
              tableRows={tableRows}
            />
          );
        blob = await pdf(pdfDoc).toBlob();
        fileExt = 'pdf';
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileOwner = sanitizeWorkListFileStem(username) || 'Work_list';
      const fileSubject =
        pdfOrXlsx === ExportFormat.XLSX
          ? 'works'
          : sanitizeWorkListFileStem(title) || 'works';
      link.download = `${fileOwner}_${fileSubject}.${fileExt}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('NETWORK_ERROR'),
      });
    }
  };

  const selectedCount = selectedDataList.length;
  const firstPageCapacity = getWorkListFirstPageCapacity(WORK_LIST_PDF_BASE_ITEMS_PER_PAGE);
  const estimatedPageCount =
    selectedCount === 0
      ? 0
      : selectedCount <= firstPageCapacity
        ? 1
        : 1 + Math.ceil((selectedCount - firstPageCapacity) / WORK_LIST_PDF_BASE_ITEMS_PER_PAGE);

  return (
    <SubContainer>
        <HeaderRow>
          <Title>{getModalMessage('WORK_LIST_PDF_EXPORT_TITLE')}</Title>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={onCloseModal}
          />
        </HeaderRow>
        {pdfOrXlsx === ExportFormat.XLSX ? (
          <WorkListExcelPreviewVertical
            tableRows={excelPreviewRows}
            includeArtist={includeArtist}
            includePrice={includePrice}
            includeNote={includeNote}
            singleLineCaption={singleLineCaption}
            captionHeaderLabel={captionHeaderLabel}
            loading={excelPreviewLoading}
          />
        ) : pdfLayout === PdfLayout.Vertical ? (
          <WorkListA4PreviewVertical
            logoSrc={logo}
            title={title}
            subtitle={subtitle}
            gallery={gallery}
            baseItemsPerPage={WORK_LIST_PDF_BASE_ITEMS_PER_PAGE}
            selectedCount={selectedCount}
            currentPage={1}
            includeArtist={includeArtist}
            includePrice={includePrice}
            includeNote={includeNote}
            pageNumberLabel={`1 / ${selectedCount === 0 ? 1 : estimatedPageCount}`}
          />
        ) : (
          <WorkListA4PreviewHorizontal
            logoSrc={logo}
            title={title}
            subtitle={subtitle}
            gallery={gallery}
            baseItemsPerPage={WORK_LIST_PDF_BASE_ITEMS_PER_PAGE}
            selectedCount={selectedCount}
            currentPage={1}
            includeArtist={includeArtist}
            includePrice={includePrice}
            includeNote={includeNote}
            pageNumberLabel={`1 / ${selectedCount === 0 ? 1 : estimatedPageCount}`}
          />
        )}
        <OptionContainer>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('WORK_LIST_PDF_PRICE_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListPdfIncludePrice"
                checked={includePrice === false}
                onChange={() => setIncludePrice(false)}
              />
              {getModalMessage('WORK_LIST_PDF_TOGGLE_EXCLUDE')}
            </CheckboxComp>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListPdfIncludePrice"
                checked={includePrice === true}
                onChange={() => setIncludePrice(true)}
              />
              {getModalMessage('WORK_LIST_PDF_TOGGLE_INCLUDE')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('WORK_LIST_PDF_NOTE_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListPdfIncludeNote"
                checked={includeNote === false}
                onChange={() => setIncludeNote(false)}
              />
              {getModalMessage('WORK_LIST_PDF_TOGGLE_EXCLUDE')}
            </CheckboxComp>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListPdfIncludeNote"
                checked={includeNote === true}
                onChange={() => setIncludeNote(true)}
              />
              {getModalMessage('WORK_LIST_PDF_TOGGLE_INCLUDE')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
        {pdfOrXlsx === ExportFormat.XLSX && (
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('WORK_LIST_PDF_CAPTION_LINE_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListPdfSingleLineCaption"
                checked={singleLineCaption === false}
                onChange={() => setSingleLineCaption(false)}
              />
              {getModalMessage('WORK_LIST_PDF_TOGGLE_EXCLUDE')}
            </CheckboxComp>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListPdfSingleLineCaption"
                checked={singleLineCaption === true}
                onChange={() => setSingleLineCaption(true)}
              />
              {getModalMessage('WORK_LIST_PDF_TOGGLE_INCLUDE')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
        )}
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('WORK_LIST_EXPORT_FORMAT_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListExportFormat"
                checked={pdfOrXlsx === ExportFormat.PDF}
                onChange={() => {
                  setPdfOrXlsx(ExportFormat.PDF);
                  setSingleLineCaption(false);
                  if (exportContentMode === BILINGUAL_EXPORT_MODE) {
                    setExportContentMode(WorkContentMode.Orig);
                  }
                }}
              />
              {getModalMessage('WORK_LIST_EXPORT_FORMAT_PDF')}
            </CheckboxComp>
            <CheckboxComp>
              <UniversalCheckbox
                type="radio"
                name="workListExportFormat"
                checked={pdfOrXlsx === ExportFormat.XLSX}
                onChange={() => setPdfOrXlsx(ExportFormat.XLSX)}
              />
              {getModalMessage('WORK_LIST_EXPORT_FORMAT_EXCEL')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('EXPORT_LANGUAGE_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <RadioBox
                type="radio"
                name="workListExportLanguage"
                checked={exportContentMode === WorkContentMode.Orig}
                onChange={() => setExportContentMode(WorkContentMode.Orig)}
              />
              {getModalMessage('EXPORT_LANGUAGE_ORIGINAL')}
            </CheckboxComp>
            <CheckboxComp>
              <RadioBox
                type="radio"
                name="workListExportLanguage"
                checked={exportContentMode === WorkContentMode.ENG}
                onChange={() => setExportContentMode(WorkContentMode.ENG)}
              />
              {getModalMessage('EXPORT_LANGUAGE_ENGLISH')}
            </CheckboxComp>
            {pdfOrXlsx === ExportFormat.XLSX && (
              <CheckboxComp>
                <RadioBox
                  type="radio"
                  name="workListExportLanguage"
                  checked={exportContentMode === BILINGUAL_EXPORT_MODE}
                  onChange={() => setExportContentMode(BILINGUAL_EXPORT_MODE)}
                />
                {getModalMessage('EXPORT_LANGUAGE_BOTH')}
              </CheckboxComp>
            )}
          </RadioGroup>
        </PDFInfoRow>
        {pdfOrXlsx === ExportFormat.PDF && (
          <>
          <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('ABOUT_PDF_LAYOUT_LABEL')}</PDFInfoLabel>
          <RadioGroup>
            <CheckboxComp>
              <RadioBox
                type="radio"
                name="aboutCvPdfLayout"
                checked={pdfLayout === PdfLayout.Vertical}
                onChange={() => hadleChangePdfLayout(PdfLayout.Vertical)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_PORTRAIT')}
            </CheckboxComp>
            <CheckboxComp>
              <RadioBox
                type="radio"
                name="aboutCvPdfLayout"
                checked={pdfLayout === PdfLayout.Horizontal}
                onChange={() => hadleChangePdfLayout(PdfLayout.Horizontal)}
              />
              {getModalMessage('ABOUT_PDF_LAYOUT_LANDSCAPE')}
            </CheckboxComp>
          </RadioGroup>
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>Title</PDFInfoLabel>
          <HeadlessInput
            value={title}
            placeholder="Title"
            handleChange={(e) => setTitle(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>Subtitle</PDFInfoLabel>
          <HeadlessInput
            value={subtitle}
            placeholder="Subtitle"
            handleChange={(e) => setSubtitle(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>Gallery</PDFInfoLabel>
          <HeadlessInput
            value={gallery}
            placeholder="Gallery"
            handleChange={(e) => setGallery(e.target.value)}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </PDFInfoRow>
        <PDFInfoRow>
          <PDFInfoLabel>{getModalMessage('WORK_LIST_PDF_SUMMARY_LABEL')}</PDFInfoLabel>
          {getModalMessage('WORK_LIST_PDF_SELECTION_SUMMARY', {
            count: selectedCount,
            pages: estimatedPageCount,
          })}
        </PDFInfoRow>
        </>
        )}
          </OptionContainer>
          <ButtonContainer>
          <HeadlessBtn
              value={getBtnName('BEFORE')}
              handleClick={onBack}
              StyledBtn={PaperBlurFitBtn}
            />
            <HeadlessBtn
              value={getBtnName('GENERATE')}
              handleClick={handleGenerate}
              StyledBtn={BlackFitBtn}
            />
          </ButtonContainer>
    </SubContainer>
  );
};

const SubContainer = styled.div`
  position: relative;
  width: 100%;
  max-height: calc(90vh - 40px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Title = styled.div`
  min-width: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1vh;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const PDFInfoRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const PDFInfoLabel = styled.span`
  min-width: 70px;
  padding: 2px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1vw;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const RadioBox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-right: 10px;
  outline: none;
  transition: border 0.2s, background 0.2s;
  position: relative;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: -3px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.color_Gray_02};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default WorkListPdfModalCreate;
